import { ChevronDown, X } from "lucide-mui";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import ButtonBase from "@mui/material/ButtonBase";
import Popover from "@mui/material/Popover";
import Typography from "@mui/material/Typography";
import { useEffect, useRef, useState, type ReactNode } from "react";
import BooleanEditor from "./editors/BooleanEditor.tsx";
import DateRangeEditor from "./editors/DateRangeEditor.tsx";
import OptionsEditor from "./editors/OptionsEditor.tsx";
import RangeEditor from "./editors/RangeEditor.tsx";
import TextEditor from "./editors/TextEditor.tsx";
import type { FilterSpec } from "./specs.ts";
import type { Filters } from "./useFilters.ts";

interface FilterPillProps {
  spec: FilterSpec;
  filters: Filters;
  /** Open now (just added from "+ Filter"). */
  openRequested?: boolean;
  onOpenHandled?: () => void;
}

/**
 * One filter as a pill: "Country ▾" while empty, "Country: Norway +1 ✕" when
 * set; click opens its editor in a popover. A pill at its default value reads
 * quietly and has no ✕ ("Match: ≥ 3").
 */
export default function FilterPill({ spec, filters, openRequested, onOpenHandled }: FilterPillProps) {
  const ref = useRef<HTMLDivElement>(null);
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);

  useEffect(() => {
    if (openRequested && ref.current) {
      setAnchor(ref.current);
      onOpenHandled?.();
    }
  }, [openRequested, onOpenHandled]);

  const close = () => {
    setAnchor(null);
    filters.release(spec.key);
  };

  const active = filters.isActive(spec);
  const summary = filters.summary(spec);
  const isDefault = filters.isDefault(spec);
  // The ✕ clears a set filter, or takes an added empty one away again.
  const removable = active || (!spec.pinned && !isDefault);
  const editable = spec.kind !== "custom" || !!spec.renderEditor;

  let editor: ReactNode = null;
  switch (spec.kind) {
    case "options":
      editor = (
        <OptionsEditor
          spec={spec}
          values={filters.optionValues(spec)}
          onChange={(v) => filters.setOptionValues(spec, v)}
          onLabel={(value, label) => filters.rememberLabel(spec, value, label)}
          onDone={close}
        />
      );
      break;
    case "boolean":
      editor = (
        <BooleanEditor spec={spec} value={filters.booleanValue(spec)} onChange={(v) => filters.setBoolean(spec, v)} />
      );
      break;
    case "range":
      editor = (
        <RangeEditor
          spec={spec}
          min={filters.text(spec.minParam)}
          max={spec.maxParam ? filters.text(spec.maxParam) : ""}
          onMinChange={(v) => filters.setText(spec.minParam, v)}
          onMaxChange={(v) => spec.maxParam && filters.setText(spec.maxParam, v)}
        />
      );
      break;
    case "dateRange":
      editor = (
        <DateRangeEditor
          spec={spec}
          from={filters.get(spec.fromParam) ?? ""}
          to={spec.toParam ? (filters.get(spec.toParam) ?? "") : ""}
          onChange={(from, to) =>
            filters.update({ [spec.fromParam]: from, ...(spec.toParam ? { [spec.toParam]: to } : {}) })
          }
        />
      );
      break;
    case "text":
      editor = (
        <TextEditor
          spec={spec}
          value={filters.text(spec.param)}
          onChange={(v) => filters.setText(spec.param, v)}
          onDone={close}
        />
      );
      break;
    case "custom":
      editor = spec.renderEditor?.({ get: filters.get, set: filters.update, close });
      break;
  }

  return (
    <>
      <Box
        ref={ref}
        sx={{
          display: "inline-flex",
          alignItems: "center",
          height: 32,
          maxWidth: 360,
          borderRadius: 999,
          border: 1,
          borderColor: active ? "rgba(var(--mui-palette-primary-mainChannel) / 0.5)" : "divider",
          bgcolor: active ? "rgba(var(--mui-palette-primary-mainChannel) / 0.08)" : "transparent",
          transition: "background-color 120ms, border-color 120ms",
          "&:hover": { borderColor: active ? "primary.main" : "text.secondary" },
        }}
      >
        <ButtonBase
          onClick={() => editable && setAnchor(ref.current)}
          aria-haspopup={editable ? "dialog" : undefined}
          aria-expanded={!!anchor}
          disabled={!editable}
          sx={{
            height: "100%",
            borderRadius: 999,
            pl: 1.5,
            pr: removable ? 0.5 : 1,
            gap: 0.5,
            minWidth: 0,
            typography: "body2",
            "&.Mui-disabled": { color: "inherit" },
          }}
        >
          <Box component="span" sx={{ color: active ? "text.primary" : "text.secondary", whiteSpace: "nowrap" }}>
            {spec.label}
            {summary !== null && ":"}
          </Box>
          {summary !== null && (
            <Box
              component="span"
              sx={{
                fontWeight: 600,
                color: active ? "primary.main" : "text.primary",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {summary}
            </Box>
          )}
          {!removable && editable && <ChevronDown sx={{ fontSize: 14, color: "text.secondary", ml: 0.25 }} />}
        </ButtonBase>
        {removable && (
          <ButtonBase
            aria-label={active ? `Clear ${spec.label} filter` : `Remove ${spec.label} filter`}
            onClick={() => filters.clear(spec)}
            sx={{
              mr: 0.75,
              ml: 0.25,
              p: 0.25,
              borderRadius: "50%",
              color: active ? "primary.main" : "text.secondary",
              "&:hover": { bgcolor: "action.hover" },
            }}
          >
            <X sx={{ fontSize: 14 }} />
          </ButtonBase>
        )}
      </Box>
      <Popover
        open={!!anchor}
        anchorEl={anchor}
        onClose={close}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        slotProps={{ paper: { sx: { mt: 0.75, width: spec.kind === "range" ? 320 : 280, borderRadius: 2 } } }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            px: 1.5,
            pt: 1.25,
            pb: spec.kind === "options" ? 0 : 0.25,
          }}
        >
          <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
            {spec.label}
          </Typography>
          {active && (
            <Button size="small" onClick={() => filters.clear(spec, { keepPill: true })} sx={{ minWidth: 0, px: 1, py: 0 }}>
              {filters.hasDefault(spec) ? "Reset" : "Clear"}
            </Button>
          )}
        </Box>
        {editor}
      </Popover>
    </>
  );
}
